import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Tip } from '@/components/shared/tip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { formatDate, formatMoney } from '@/utils/format';

import { AdjustPlanDialog, canShowAdjustPlanDialog } from './adjust-plan-dialog';
import { AssignedCouponsCard } from './assigned-coupons-card';
import { CurrentPlanDetails } from './current-plan-details';
import { isTrialing } from './subscription-checkout-access';
import type { AssignedCoupon, PaidPlan, SubscriptionsSummary } from './subscriptions-types';
import { useSubscriptionsContext } from './use-subscriptions-context';

const quoteCurrentPlanPrice = (summary: SubscriptionsSummary): string | null => {
  const { subscription, checkout } = summary;
  if (subscription.plan !== 'essential' && subscription.plan !== 'professional') return null;
  const planQuote = checkout?.plans?.find((row) => row.plan === subscription.plan);
  if (!planQuote) return null;
  const interval = subscription.billingInterval ?? 'annual';
  const amountMinor =
    interval === 'monthly' ? planQuote.monthlyAmountMinor : planQuote.annualAmountMinor;
  return formatMoney(amountMinor, planQuote.currency);
};

export const SubscriptionsOverviewPage = () => {
  const { t } = useTranslation('billing');
  const { t: tErrors } = useTranslation('errors');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { summary, summaryLoading } = useSubscriptionsContext();
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [redeemCode, setRedeemCode] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  const assignedCoupons = useQuery({
    queryKey: ['billing-assigned-coupons'],
    queryFn: () =>
      api.get<{ data: AssignedCoupon[]; page: OffsetPage }>('/billing/coupons/assigned', {
        query: { page: 1, pageSize: 20 },
      }),
  });

  useEffect(() => {
    if (searchParams.get('adjust') === '1' && summary && canShowAdjustPlanDialog(summary)) {
      setAdjustOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('adjust');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, summary]);

  const invalidateBilling = async () => {
    await queryClient.invalidateQueries({ queryKey: ['billing-summary'] });
  };

  const cancelSubscription = useMutation({
    mutationFn: () => api.post('/billing/subscription/cancel'),
    onSuccess: async () => {
      setCancelOpen(false);
      await invalidateBilling();
      toast.success(t('management.cancelScheduled'));
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const resumeSubscription = useMutation({
    mutationFn: () => api.post('/billing/subscription/resume'),
    onSuccess: async () => {
      await invalidateBilling();
      toast.success(t('management.cancelUndone'));
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const clearScheduledChange = useMutation({
    mutationFn: () => api.post('/billing/subscription/clear-scheduled-change'),
    onSuccess: async () => {
      await invalidateBilling();
      toast.success(t('management.scheduledChangeCleared'));
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const planAction = useMemo(() => {
    if (!summary || !canShowAdjustPlanDialog(summary)) return null;
    const canAdjust = summary.management?.canAdjustPlan ?? false;
    return {
      label: canAdjust ? t('management.adjustPlanLink') : t('checkout.title'),
      canOpen: true,
    };
  }, [summary, t]);

  if (summaryLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!summary) return null;

  const accessInfo = summary.access;
  const management = summary.management;
  const periodEnd = summary.subscription.currentPeriodEnd;
  const onTrial = isTrialing(summary);
  const trialEndsAt = summary.subscription.trialEndsAt ?? (onTrial ? periodEnd : null);
  const canRenewCurrentPlan = management?.canRenewCurrentPlan ?? false;
  const currentPrice = quoteCurrentPlanPrice(summary);
  const billingIntervalLabel =
    summary.subscription.billingInterval === 'monthly'
      ? t('checkout.perMonth')
      : summary.subscription.billingInterval === 'annual'
        ? t('checkout.perYear')
        : null;

  return (
    <div className="space-y-6">
      {accessInfo && !accessInfo.consoleWritable && accessInfo.refusalCode ? (
        <Card className="border-warning/40">
          <CardHeader>
            <CardTitle>{t('readOnly.title')}</CardTitle>
            <CardDescription>{t('readOnly.body')}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-body-sm text-fg-secondary">{tErrors(accessInfo.refusalCode)}</p>
            <Tip tipId="billing.readOnly" />
          </CardContent>
        </Card>
      ) : null}

      {management?.canAdjustPlan && summary.subscription.cancelAtPeriodEnd && periodEnd ? (
        <Card className="border-warning/40">
          <CardHeader>
            <CardTitle>{t('management.cancelPendingTitle')}</CardTitle>
            <CardDescription>
              {t('management.cancelPendingDescription', { date: formatDate(periodEnd) })}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              loading={resumeSubscription.isPending}
              onClick={() => resumeSubscription.mutate()}
            >
              {t('management.keepSubscription')}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {management?.pendingPlan && periodEnd ? (
        <Card className="border-brand/30">
          <CardHeader>
            <CardTitle>{t('management.downgradePendingTitle')}</CardTitle>
            <CardDescription>
              {t('management.downgradePendingDescription', {
                plan: t(`plans.${management.pendingPlan as PaidPlan}`),
                date: formatDate(periodEnd),
              })}
            </CardDescription>
          </CardHeader>
          {management.canClearScheduledChange ? (
            <CardContent className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                loading={clearScheduledChange.isPending}
                onClick={() => clearScheduledChange.mutate()}
              >
                {t('management.clearScheduledChange')}
              </Button>
            </CardContent>
          ) : null}
        </Card>
      ) : null}

      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <CardTitle>{t('overview.currentPlanTitle')}</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-heading-sm font-semibold text-fg">
                {t(`plans.${summary.subscription.plan}`)}
              </p>
              <Badge
                variant={
                  summary.subscription.status === 'active' ||
                  summary.subscription.status === 'trialing'
                    ? 'success'
                    : summary.subscription.status === 'past_due' ||
                        summary.subscription.status === 'grace' ||
                        summary.subscription.status === 'suspended'
                      ? 'warning'
                      : 'neutral'
                }
              >
                {t(`statuses.${summary.subscription.status}`)}
              </Badge>
            </div>
            {currentPrice && billingIntervalLabel ? (
              <p className="text-body-sm text-fg-secondary">
                {currentPrice}
                <span className="text-caption"> {billingIntervalLabel}</span>
              </p>
            ) : null}
            <CardDescription className="text-body-sm">
              {summary.subscription.cancelAtPeriodEnd && periodEnd
                ? t('management.cancelPendingDescription', { date: formatDate(periodEnd) })
                : onTrial && trialEndsAt
                  ? t('trialEndsOn', { date: formatDate(trialEndsAt) })
                  : canRenewCurrentPlan && periodEnd
                    ? t('overview.renewAvailable', { date: formatDate(periodEnd) })
                    : periodEnd
                      ? t('periodEndsOn', { date: formatDate(periodEnd) })
                      : t('overview.currentPlanDescription')}
            </CardDescription>
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            {canRenewCurrentPlan && planAction?.canOpen ? (
              <Button type="button" onClick={() => setAdjustOpen(true)}>
                {t('checkout.renew')}
              </Button>
            ) : null}
            {planAction?.canOpen ? (
              <Button
                type="button"
                variant={canRenewCurrentPlan ? 'outline' : 'primary'}
                onClick={() => setAdjustOpen(true)}
              >
                {planAction.label}
              </Button>
            ) : null}
          </div>
        </CardHeader>
      </Card>

      <AssignedCouponsCard
        coupons={assignedCoupons.data?.data ?? []}
        canRedeem={canShowAdjustPlanDialog(summary)}
        onRedeem={(code) => {
          setRedeemCode(code);
          setAdjustOpen(true);
        }}
      />

      <CurrentPlanDetails summary={summary} />

      <Card>
        <CardHeader>
          <CardTitle>{t('overview.usageTitle')}</CardTitle>
          <CardDescription>{t('overview.usageDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-body-sm text-fg-secondary">
          <p>{t('seatsHelp')}</p>
          {summary.storage ? (
            <p>
              {summary.storage.quotaBytes === null ? t('storageHelpConsult') : t('storageHelp')}
            </p>
          ) : null}
          <Tip tipId="billing.seats" />
          {summary.storage ? <Tip tipId="billing.storage" /> : null}
        </CardContent>
      </Card>

      {management?.canCancel ? (
        <Card>
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <CardTitle>{t('management.cancelTitle')}</CardTitle>
              <CardDescription>{t('management.cancelDescription')}</CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              className="shrink-0"
              onClick={() => setCancelOpen(true)}
            >
              {t('management.cancelAction')}
            </Button>
          </CardHeader>
        </Card>
      ) : null}

      {canShowAdjustPlanDialog(summary) ? (
        <AdjustPlanDialog
          open={adjustOpen}
          onOpenChange={(next) => {
            setAdjustOpen(next);
            if (!next) setRedeemCode(null);
          }}
          summary={summary}
          initialCouponCode={redeemCode}
        />
      ) : null}

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={t('management.cancelConfirmTitle')}
        description={t('management.cancelConfirmDescription', {
          date: periodEnd ? formatDate(periodEnd) : '',
        })}
        confirmLabel={t('management.cancelAction')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={cancelSubscription.isPending}
        onConfirm={() => cancelSubscription.mutate()}
      />
    </div>
  );
};
